import { calculateDiscountPriceWithCartItems, roundingNumber } from '../../../../utils';
import { DISCOUNT_TYPE, LOCAL_COUPON } from '../../../../constants';
import { VOUCHER_CART_TYPE } from '../../../../constants/voucher';

import {
     SET_CART,
     EMPTY_CART,
     SET_CART_ITEMS,
     REMOVE_CART_ITEM,
     ADD_CART_ITEM,
     ADD_CART_COUPON,
     REMOVE_CART_COUPON,
     ADD_CART_VOUCHER_RULE,
     REMOVE_CART_VOUCHER_RULE,
     ADD_CART_COUPON_VOUCHERS,
     SET_CART_COUPON_VOUCHERS,
     REMOVE_CART_COUPON_VOUCHERS,
     CLEAR_CART,
     CLEAR_CART_COUPON_VOUCHERS,
} from './actions';
import { standardizeCouponVouchers } from '../../../../utils/couponVoucher';

const INITIAL_STATE_CART = {
     cart: {},
     items: [],
     coupon: {},
     voucherRule: {},
     couponVouchers: [],
};

const calculateCartDiscountWithDiscountOnCart = (payload) => {
     const { cartItems, discountType, discountValue, validProductIds } = payload;
     let cartDiscount = 0;

     if (discountType === DISCOUNT_TYPE.PRICE) {
          cartDiscount = cartItems.reduce((cartItemAcc, currCartItem) => {
               if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                    const itemCouponDiscount = Math.min(discountValue, currCartItem.total) || 0;

                    return cartItemAcc + itemCouponDiscount;
               }

               return cartItemAcc;
          }, 0);

          if (cartDiscount > discountValue) cartDiscount = discountValue;
     } else {
          const cartDiscountTotal =
               cartItems.reduce((cartItemAcc, currCartItem) => {
                    if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                         return cartItemAcc + currCartItem.total;
                    }

                    return cartItemAcc;
               }, 0) || 1;

          cartDiscount = (cartDiscountTotal * discountValue) / 100;
     }

     return cartDiscount;
};

const calculateCartDiscountWithDiscountOnProduct = (payload) => {
     const { cartItems, discountType, discountValue, validProductIds } = payload;
     let cartDiscount = 0;

     if (discountType === DISCOUNT_TYPE.PRICE) {
          cartDiscount = cartItems.reduce((cartItemAcc, currCartItem) => {
               if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                    let itemCouponDiscount = discountValue * currCartItem.quantity;

                    if (itemCouponDiscount > currCartItem.total) {
                         itemCouponDiscount = currCartItem.total;
                    }

                    return cartItemAcc + itemCouponDiscount;
               }

               return cartItemAcc;
          }, 0);
     } else {
          const cartDiscountTotal =
               cartItems.reduce((cartItemAcc, currCartItem) => {
                    if (!validProductIds.length || validProductIds.includes(currCartItem.productId)) {
                         return cartItemAcc + currCartItem.total;
                    }

                    return cartItemAcc;
               }, 0) || 1;

          cartDiscount = (cartDiscountTotal * discountValue) / 100;
     }

     return cartDiscount;
};

const calculateCouponPayloadSetCart = ({ cartItems, coupon }) => {
     const validProductIds = coupon?.validProductIds?.filter((productId) => cartItems.some((cartItem) => cartItem.productId === productId)) ?? [];

     let couponPayload = {
          ...coupon,
          ...(validProductIds.length && { validProductIds }),
     };

     if (!validProductIds.length && coupon?.validProductIds?.length) {
          localStorage.removeItem(LOCAL_COUPON);
          couponPayload = {};
     }

     return couponPayload;
};

const calculateVouchersPayloadSetCart = ({ cartItems, couponVouchers }) => {
     if (!couponVouchers?.length) return [];

     const vouchersPayload = [];

     for (let i = 0; i < couponVouchers.length; i++) {
          const voucher = couponVouchers[i];

          const validProductIds =
               voucher.validProductIds?.filter((productId) => cartItems.some((cartItem) => cartItem.productId === productId)) ?? [];

          const voucherPayload = {
               ...voucher,
               ...(validProductIds.length && { validProductIds: voucher.validProductIds }),
          };

          if (!validProductIds.length && voucher.validProductIds?.length) {
               voucherPayload.isInvalid = true;
          }

          vouchersPayload.push(voucherPayload);
     }

     return vouchersPayload;
};

const processCart = ({ cart, cartItems, couponVouchers }) => {
     const newCartItems = calculateDiscountPriceWithCartItems(cartItems);

     if (cart && newCartItems.length > 0) {
          const cartSubTotal = newCartItems.reduce((accCartItem, cartItem) => accCartItem + cartItem.total, 0);

          let cartDiscount = 0;
          const hasVouchersDiscount = !!couponVouchers?.length;

          if (hasVouchersDiscount) {
               for (let i = 0; i < couponVouchers.length; i++) {
                    const voucher = couponVouchers[i];

                    if (voucher.isInvalid) continue;

                    const validProductIds = voucher.validProductIds || [];
                    const voucherDiscount = Number(voucher.discountValue) || 0;

                    // For vouchers, only the cart type is applicable on the cart
                    if (voucher.category === 'voucher') voucher.cartType = VOUCHER_CART_TYPE.DISCOUNT_ON_CART;

                    switch (voucher.cartType) {
                         case VOUCHER_CART_TYPE.DISCOUNT_ON_CART:
                              {
                                   const _cartDiscount = calculateCartDiscountWithDiscountOnCart({
                                        cartItems: newCartItems,
                                        discountType: voucher.discountType,
                                        discountValue: voucherDiscount,
                                        validProductIds,
                                   });

                                   cartDiscount += _cartDiscount;
                              }
                              break;
                         case VOUCHER_CART_TYPE.DISCOUNT_ON_PRODUCT:
                              {
                                   const _cartDiscount = calculateCartDiscountWithDiscountOnProduct({
                                        cartItems: newCartItems,
                                        discountType: voucher.discountType,
                                        discountValue: voucherDiscount,
                                        validProductIds,
                                   });

                                   cartDiscount += _cartDiscount;
                              }
                              break;
                         default:
                              break;
                    }
               }
          }

          cart.subTotal = roundingNumber(cartSubTotal);
          cart.discount = Math.min(cartDiscount, cartSubTotal) || 0;
          cart.total = roundingNumber(cartSubTotal - cart.discount);
     }

     return { cart, cartItems: newCartItems };
};

export const cartReducer = (state = INITIAL_STATE_CART, action) => {
     switch (action.type) {
          case SET_CART: {
               const payload = { ...action.payload };

               if (!Object.keys(payload).length) {
                    return INITIAL_STATE_CART;
               }

               if (payload.cartItems?.length > 0) {
                    const cartItemsPayload = [...payload.cartItems];
                    const coupon = { ...state.coupon };
                    const couponVouchers = [...state.couponVouchers];

                    delete payload.cartItems;

                    const couponPayload = calculateCouponPayloadSetCart({ cartItems: cartItemsPayload, coupon });
                    const vouchersPayload = calculateVouchersPayloadSetCart({ cartItems: cartItemsPayload, couponVouchers });

                    const { cart, cartItems } = processCart({
                         cart: payload,
                         cartItems: cartItemsPayload,
                         coupon: couponPayload,
                         couponVouchers: vouchersPayload,
                    });

                    return {
                         ...state,
                         cart,
                         items: cartItems,
                         coupon: couponPayload,
                         couponVouchers: vouchersPayload,
                    };
               }

               return {
                    ...state,
                    cart: payload,
                    items: [],
               };
          }
          case EMPTY_CART: {
               return {
                    ...state,
                    items: [],
                    coupon: {},
                    couponVouchers: [],
               };
          }
          case REMOVE_CART_ITEM: {
               return action.payload;
          }
          case SET_CART_ITEMS: {
               return {
                    ...state,
                    items: action.payload,
               };
          }
          case ADD_CART_ITEM: {
               return action.payload;
          }
          case ADD_CART_COUPON: {
               const validProductIds =
                    action.payload.validProductIds?.filter((productId) => state.items.some((cartItem) => cartItem.productId === productId)) ?? [];

               let couponPayload = {
                    ...action.payload,
                    ...(validProductIds.length && { validProductIds }),
               };

               if (!validProductIds.length && action.payload.validProductIds?.length) {
                    localStorage.removeItem(LOCAL_COUPON);
                    couponPayload = {};
               }

               const { cart, cartItems } = processCart({
                    cart: state.cart,
                    cartItems: state.items,
                    coupon: couponPayload,
                    couponVouchers: state.couponVouchers,
               });

               return {
                    ...state,
                    cart,
                    items: cartItems,
                    coupon: couponPayload,
               };
          }
          case REMOVE_CART_COUPON: {
               const { cart, cartItems } = processCart({ cart: state.cart, cartItems: state.items, couponVouchers: state.couponVouchers });

               return {
                    ...state,
                    cart,
                    items: cartItems,
                    coupon: {},
               };
          }
          case ADD_CART_VOUCHER_RULE: {
               if (action.payload) state.voucherRule = action.payload;
               return {
                    ...state,
               };
          }
          case REMOVE_CART_VOUCHER_RULE: {
               return {
                    ...state,
                    voucherRule: {},
               };
          }
          case ADD_CART_COUPON_VOUCHERS: {
               const newCodes = state.couponVouchers || [];

               if (Array.isArray(action.payload)) {
                    newCodes.push(...action.payload);
               } else newCodes.push(action.payload);

               const standardizedCodes = standardizeCouponVouchers(newCodes);

               return {
                    ...state,
                    couponVouchers: standardizedCodes,
               };
          }
          case SET_CART_COUPON_VOUCHERS: {
               const newCodes = action.payload || [];

               const standardizedCodes = standardizeCouponVouchers(newCodes);

               return {
                    ...state,
                    couponVouchers: standardizedCodes,
               };
          }
          case REMOVE_CART_COUPON_VOUCHERS: {
               const code = action.payload;
               const newCodes = state.couponVouchers?.filter((couponVoucher) => couponVoucher.code !== code) || [];

               const findCoupon = state.couponVouchers?.filter((couponVoucher) => couponVoucher.code === code && couponVoucher.category === 'coupon');

               return {
                    ...state,
                    coupon: findCoupon ? {} : state.coupon,
                    couponVouchers: newCodes,
               };
          }
          case CLEAR_CART_COUPON_VOUCHERS: {
               return {
                    ...state,
                    couponVouchers: [],
               };
          }
          case CLEAR_CART: {
               return INITIAL_STATE_CART;
          }
          default:
               return state;
     }
};
